import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getStoredSupabaseSession, withTimeout } from "@/lib/supabaseAuthStability";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(() => getStoredSupabaseSession());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // 1) Listener primeiro (fonte da verdade para mudanças de sessão)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!mounted) return;
        setSession(newSession);
        setLoading(false);
      }
    );

    // 2) Hidratação inicial — não bloqueia indefinidamente
    withTimeout(
      supabase.auth.getSession(),
      3000,
      "Tempo esgotado ao restaurar a sessão."
    )
      .then(({ data: { session: initial } }) => {
        if (!mounted) return;
        setSession(initial ?? getStoredSupabaseSession());
        setLoading(false);
      })
      .catch((err) => {
        console.error("Auth session hydration error", err);
        if (!mounted) return;
        setLoading(false);
      });

    // Fallback: se nada respondeu em 3s, libera a UI
    const safety = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 3000);

    return () => {
      mounted = false;
      clearTimeout(safety);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      await withTimeout(supabase.auth.signOut(), 5000, "Tempo esgotado ao sair.");
    } finally {
      setSession(null);
    }
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
